import React, { useState, useMemo, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { getIn } from "formik";

import { VariableSelector } from "./VariableSelector";
import { PercentageField } from "./PercentageField";
import { BaseParametersRenderer } from "./BaseParametersRenderer";

import type {
  AdvancedVariableParams,
  SilaeFormData,
  SilaeVariable,
} from "@/types/silae";
import { useSilaeVariables } from "@/hooks/useSilaeVariables";

interface SilaeVariableConfigProps {
  formik: FormikProps<SilaeFormData>;
  sourceFieldPrefix: string; // Ex: "ifBranch.bonuses.0.bases.0"
}

/**
 * SilaeVariableConfig
 *
 * Composant réutilisable pour configurer une variable SILAE avancée.
 * Supporte la nouvelle structure hiérarchique simplifiée avec Formik.
 *
 * Utilise uniquement:
 * - formik: Pour accéder et mettre à jour les valeurs
 * - sourceFieldPrefix: Chemin complet vers la source (ex: "ifBranch.bonuses.0.bases.0")
 *
 * Tous les champs sont accédés via getIn(formik.values, `${sourceFieldPrefix}.fieldName`)
 */
export const SilaeVariableConfig: React.FC<SilaeVariableConfigProps> = ({
  formik,
  sourceFieldPrefix,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);

  // Hook pour les variables Silae depuis Redux
  const {
    variables: silaeVariables,
    searchVariables,
    findVariable,
    frequentlyUsedVariables: allFrequentlyUsedVariables,
  } = useSilaeVariables();

  // Récupérer la variable sélectionnée via getIn
  const advancedVariable = getIn(
    formik.values,
    `${sourceFieldPrefix}.advancedVariable`,
  ) as string | undefined;

  const advancedVariablePercentage = getIn(
    formik.values,
    `${sourceFieldPrefix}.advancedVariablePercentage`,
  ) as string | undefined;

  // Récupérer les valeurs pour les opérations mathématiques supplémentaires
  const advancedVariableEnableAdditional = getIn(
    formik.values,
    `${sourceFieldPrefix}.advancedVariableEnableAdditional`,
  ) as boolean | undefined;

  const advancedVariableAdditionalOperator = getIn(
    formik.values,
    `${sourceFieldPrefix}.advancedVariableAdditionalOperator`,
  ) as string | undefined;

  const advancedVariableAdditionalValue = getIn(
    formik.values,
    `${sourceFieldPrefix}.advancedVariableAdditionalValue`,
  ) as string | undefined;

  // Récupérer la valeur mtpartValue pour les variables mtpart.xxxx
  const advancedVariableMtpartValue = getIn(
    formik.values,
    `${sourceFieldPrefix}.advancedVariableMtpartValue`,
  ) as string | undefined;

  const advancedVariablePostCallVarName = getIn(
    formik.values,
    `${sourceFieldPrefix}.advancedVariablePostCallVarName`,
  ) as string | undefined;

  const advancedVariablePostCallExpression = getIn(
    formik.values,
    `${sourceFieldPrefix}.advancedVariablePostCallExpression`,
  ) as string | undefined;

  const selectedVariable = useMemo(() => {
    return advancedVariable ? findVariable(advancedVariable) : null;
  }, [advancedVariable, findVariable]);

  // Vrai si la variable fonction a des variables de sortie → afficher les champs post-call
  const hasOutputParams = useMemo(() => {
    return (
      selectedVariable?.type === "function" &&
      (selectedVariable.parameters ?? []).some((p) => p.isOutputParam)
    );
  }, [selectedVariable]);

  // Build preview string according to new parameter structure
  // Gérer l'activation/désactivation des opérations additionnelles
  const handleAdditionalToggle = useCallback(
    (checked: boolean) => {
      formik.setFieldValue(
        `${sourceFieldPrefix}.advancedVariableEnableAdditional`,
        checked,
      );
      // Initialiser les valeurs par défaut si on active
      if (checked) {
        if (!advancedVariableAdditionalOperator) {
          formik.setFieldValue(
            `${sourceFieldPrefix}.advancedVariableAdditionalOperator`,
            "+",
          );
        }
        if (!advancedVariableAdditionalValue) {
          formik.setFieldValue(
            `${sourceFieldPrefix}.advancedVariableAdditionalValue`,
            "0",
          );
        }
      }
    },
    [
      formik,
      sourceFieldPrefix,
      advancedVariableAdditionalOperator,
      advancedVariableAdditionalValue,
    ],
  );

  // Variables fréquemment utilisées (int, double, function)
  const frequentlyUsedVariables = useMemo(() => {
    return allFrequentlyUsedVariables.filter(
      (variable) =>
        variable.type &&
        (variable.type === "int" ||
          variable.type === "double" ||
          variable.type === "function"),
    );
  }, [allFrequentlyUsedVariables]);

  // Variables filtrées selon la recherche
  const filteredVariables = useMemo(() => {
    if (!searchTerm.trim()) {
      return silaeVariables
        .filter(
          (variable) =>
            variable.type &&
            (variable.type === "int" ||
              variable.type === "double" ||
              variable.type === "function"),
        )
        .slice(0, 50);
    }
    return searchVariables(searchTerm).filter(
      (variable) =>
        variable.type &&
        (variable.type === "int" ||
          variable.type === "double" ||
          variable.type === "function"),
    );
  }, [searchTerm]);

  // Gérer la sélection d'une variable
  const handleVariableSelect = useCallback(
    (variable: SilaeVariable) => {
      formik.setFieldValue(
        `${sourceFieldPrefix}.advancedVariable`,
        variable.code,
      );

      // Initialiser le pourcentage à 100% par défaut pour toutes les variables
      formik.setFieldValue(
        `${sourceFieldPrefix}.advancedVariablePercentage`,
        "100",
      );

      // Initialiser les paramètres si la variable en a
      if (variable.parameters && variable.parameters.length > 0) {
        const initialParameters = variable.parameters
          .filter((param: AdvancedVariableParams) => !param.isFixed)
          .map((param: AdvancedVariableParams) => {
            // Structure complète avec tous les props possibles
            const fullParam: {
              id: string;
              isRequired: boolean;
              label?: string;
              type?: string;
              includeBulletinCourant?: boolean;
              shouldIncludeBulletinCourant?: boolean;
              note?: string;
              placeholder?: string;
              helper?: string;
              isFixed?: boolean;
              fixedValue?: string;
              options?: Array<{
                label: string;
                code: string;
              }>;
              value?: string;
              percentage?: string;
            } = {
              id: param.id,
              isRequired: param.isRequired || false,
              label: param.label,
              type: param.type,
              includeBulletinCourant: param.includeBulletinCourant,
              shouldIncludeBulletinCourant: false, // Initialisé à false par défaut
              note: param.note,
              placeholder: param.placeholder,
              helper: param.helper,
              isFixed: param.isFixed,
              fixedValue: param.fixedValue,
              options: param.options,
              value: param.type === "Bool" ? "false" : "",
            };

            // Ajouter le pourcentage uniquement pour les paramètres numériques
            if (param.type === "int" || param.type === "double") {
              fullParam.percentage = "100";
            }

            return fullParam;
          });

        formik.setFieldValue(
          `${sourceFieldPrefix}.advancedVariableParameters`,
          initialParameters,
        );
      } else {
        formik.setFieldValue(
          `${sourceFieldPrefix}.advancedVariableParameters`,
          [],
        );
      }

      // Réinitialiser les opérations additionnelles
      formik.setFieldValue(
        `${sourceFieldPrefix}.advancedVariableEnableAdditional`,
        false,
      );
      formik.setFieldValue(
        `${sourceFieldPrefix}.advancedVariableAdditionalOperator`,
        "+",
      );
      formik.setFieldValue(
        `${sourceFieldPrefix}.advancedVariableAdditionalValue`,
        "0",
      );
      // Réinitialiser le post-call
      formik.setFieldValue(
        `${sourceFieldPrefix}.advancedVariablePostCallVarName`,
        "",
      );
      formik.setFieldValue(
        `${sourceFieldPrefix}.advancedVariablePostCallExpression`,
        "",
      );

      // Déclencher la validation après que Formik a batché tous les setFieldValue
      setTimeout(() => formik.validateForm(), 0);
    },
    [formik, sourceFieldPrefix],
  );

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-muted/20 dark:bg-muted/10">
      {/* En-tête avec titre et badge */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-foreground">
          {t("silaeVariableConfig.title")}
        </span>
      </div>

      {/* Description */}
      <p className="text-xs text-muted-foreground">
        {t("silaeVariableConfig.description")}
      </p>

      {/* Sélecteur de variable */}
      <VariableSelector
        selectedVariable={selectedVariable || null}
        onVariableSelect={handleVariableSelect}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        open={open}
        setOpen={setOpen}
        frequentlyUsedVariables={frequentlyUsedVariables}
        filteredVariables={filteredVariables}
      />

      {/* Champ de pourcentage pour les variables numériques */}
      {selectedVariable &&
        (selectedVariable.type === "int" ||
          selectedVariable.type === "double") && (
          <PercentageField
            formik={formik}
            fieldName={`${sourceFieldPrefix}.advancedVariablePercentage`}
            selectedVariable={selectedVariable}
          />
        )}

      {/* Rendu des paramètres si la variable en a */}
      {selectedVariable &&
        selectedVariable.parameters &&
        selectedVariable.parameters.length > 0 && (
          <BaseParametersRenderer
            formik={formik}
            selectedVariable={selectedVariable}
            fieldPrefix={`${sourceFieldPrefix}.advancedVariableParameters`}
          />
        )}

      {/* Expression post-call pour les variables de sortie */}
      {hasOutputParams && (
        <div className="space-y-3 p-3 rounded-md border border-violet-400 dark:border-violet-600 bg-violet-50/50 dark:bg-violet-950/20">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wide">
              Expression après l'appel
            </span>
            <span className="text-[10px] font-medium text-red-500 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 px-1.5 py-0.5 rounded">
              Requis
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Cette fonction ne retourne pas de valeur directe — elle modifie les
            variables de sortie. Définissez l'expression à calculer à partir de
            ces variables.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_2fr]">
            <div className="space-y-1.5">
              <Label
                htmlFor={`postCallVarName-${sourceFieldPrefix}`}
                className="text-xs"
              >
                Nom de la variable <span className="text-red-500">*</span>
              </Label>
              <Input
                id={`postCallVarName-${sourceFieldPrefix}`}
                placeholder="Ex: VAR_INTERMEDIAIRE"
                value={advancedVariablePostCallVarName || ""}
                onChange={(e) =>
                  formik.setFieldValue(
                    `${sourceFieldPrefix}.advancedVariablePostCallVarName`,
                    e.target.value,
                  )
                }
                onBlur={() =>
                  formik.setFieldTouched(
                    `${sourceFieldPrefix}.advancedVariablePostCallVarName`,
                    true,
                  )
                }
                className={
                  getIn(
                    formik.touched,
                    `${sourceFieldPrefix}.advancedVariablePostCallVarName`,
                  ) &&
                  getIn(
                    formik.errors,
                    `${sourceFieldPrefix}.advancedVariablePostCallVarName`,
                  )
                    ? "border-red-500"
                    : ""
                }
              />
              {getIn(
                formik.touched,
                `${sourceFieldPrefix}.advancedVariablePostCallVarName`,
              ) &&
                getIn(
                  formik.errors,
                  `${sourceFieldPrefix}.advancedVariablePostCallVarName`,
                ) && (
                  <p className="text-xs text-red-500">
                    {getIn(
                      formik.errors,
                      `${sourceFieldPrefix}.advancedVariablePostCallVarName`,
                    )}
                  </p>
                )}
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor={`postCallExpr-${sourceFieldPrefix}`}
                className="text-xs"
              >
                Expression (variables de sortie){" "}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id={`postCallExpr-${sourceFieldPrefix}`}
                placeholder="Ex: bs + ts - tp"
                value={advancedVariablePostCallExpression || ""}
                onChange={(e) =>
                  formik.setFieldValue(
                    `${sourceFieldPrefix}.advancedVariablePostCallExpression`,
                    e.target.value,
                  )
                }
                onBlur={() =>
                  formik.setFieldTouched(
                    `${sourceFieldPrefix}.advancedVariablePostCallExpression`,
                    true,
                  )
                }
                className={
                  getIn(
                    formik.touched,
                    `${sourceFieldPrefix}.advancedVariablePostCallExpression`,
                  ) &&
                  getIn(
                    formik.errors,
                    `${sourceFieldPrefix}.advancedVariablePostCallExpression`,
                  )
                    ? "border-red-500"
                    : ""
                }
              />
              {getIn(
                formik.touched,
                `${sourceFieldPrefix}.advancedVariablePostCallExpression`,
              ) &&
                getIn(
                  formik.errors,
                  `${sourceFieldPrefix}.advancedVariablePostCallExpression`,
                ) && (
                  <p className="text-xs text-red-500">
                    {getIn(
                      formik.errors,
                      `${sourceFieldPrefix}.advancedVariablePostCallExpression`,
                    )}
                  </p>
                )}
            </div>
          </div>
          {advancedVariablePostCallVarName &&
            advancedVariablePostCallExpression && (
              <p className="text-xs font-mono text-violet-600 dark:text-violet-400">
                {advancedVariablePostCallVarName} ={" "}
                {advancedVariablePostCallExpression}
              </p>
            )}
        </div>
      )}

      {/* Configuration mtpart.xxxx */}
      {selectedVariable && selectedVariable.code === "mtpart.xxxx" && (
        <div className="space-y-2">
          <Label htmlFor={`mtpartValue-${sourceFieldPrefix}`}>
            {t("silaeVariableConfig.mtpartValue.label")}
          </Label>
          <Input
            id={`mtpartValue-${sourceFieldPrefix}`}
            type="text"
            placeholder={t("silaeVariableConfig.mtpartValue.placeholder")}
            value={advancedVariableMtpartValue || ""}
            onChange={(e) =>
              formik.setFieldValue(
                `${sourceFieldPrefix}.advancedVariableMtpartValue`,
                e.target.value,
              )
            }
            onBlur={() =>
              formik.setFieldTouched(
                `${sourceFieldPrefix}.advancedVariableMtpartValue`,
                true,
              )
            }
          />
          <p className="text-xs text-muted-foreground">
            {t("silaeVariableConfig.mtpartValue.description")}
          </p>
        </div>
      )}

      {/* Opérations mathématiques supplémentaires */}
      {selectedVariable && (
        <div className="space-y-4 pt-2 border-t border-border">
          <div className="flex items-center space-x-2">
            <Switch
              id={`advancedVariableEnableAdditional-${sourceFieldPrefix}`}
              checked={advancedVariableEnableAdditional || false}
              onCheckedChange={handleAdditionalToggle}
            />
            <Label
              htmlFor={`advancedVariableEnableAdditional-${sourceFieldPrefix}`}
              className="text-sm font-medium text-foreground"
            >
              {t("silaeVariableConfig.additional.enable")}
            </Label>
          </div>

          {advancedVariableEnableAdditional && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-foreground">
                  {t("silaeVariableConfig.additional.operator.label")}
                </Label>
                <Select
                  value={advancedVariableAdditionalOperator || "+"}
                  onValueChange={(value) =>
                    formik.setFieldValue(
                      `${sourceFieldPrefix}.advancedVariableAdditionalOperator`,
                      value,
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="+">
                      {t("silaeVariableConfig.additional.operator.add")}
                    </SelectItem>
                    <SelectItem value="-">
                      {t("silaeVariableConfig.additional.operator.subtract")}
                    </SelectItem>
                    <SelectItem value="*">
                      {t("silaeVariableConfig.additional.operator.multiply")}
                    </SelectItem>
                    <SelectItem value="/">
                      {t("silaeVariableConfig.additional.operator.divide")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-foreground">
                  {t("silaeVariableConfig.additional.value.label")}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={t(
                    "silaeVariableConfig.additional.value.placeholder",
                  )}
                  value={advancedVariableAdditionalValue || ""}
                  onChange={(e) =>
                    formik.setFieldValue(
                      `${sourceFieldPrefix}.advancedVariableAdditionalValue`,
                      e.target.value,
                    )
                  }
                  onBlur={() =>
                    formik.setFieldTouched(
                      `${sourceFieldPrefix}.advancedVariableAdditionalValue`,
                      true,
                    )
                  }
                />
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
};
